import { useCallback, type MutableRefObject } from "react";
import { getContact, type MessageRow } from "../../../lib/db";
import type { ServerMessageRow } from "../../../lib/authApi";
import {
  b64ToU8,
  decryptDirectMessage,
  TOMBSTONE_ENVELOPE_PLAINTEXT,
  type IdentityKeys,
} from "../../../lib/crypto";
import { dmPeerIdFromConversation, type ConversationRef } from "../../state/chatState";

export function useDecodeServerMessageRow(
  keysRef: MutableRefObject<IdentityKeys | null>,
  selectedConversationRef: MutableRefObject<ConversationRef | null>,
) {
  return useCallback(
    async (row: ServerMessageRow): Promise<MessageRow | null> => {
      const activeKeys = keysRef.current;
      if (!activeKeys) return null;
      const peerId = row.from_id === activeKeys.userId ? row.to_id : row.from_id;
      const peer = await getContact(peerId);
      if (!peer) return null;

      if (row.deleted === true) {
        const focused = dmPeerIdFromConversation(selectedConversationRef.current) === peerId;
        const isOutgoing = row.from_id === activeKeys.userId;
        const seenLocal = isOutgoing ? focused : focused || row.read === true;
        return {
          id: row.id,
          peerId,
          from_id: row.from_id,
          to_id: row.to_id,
          created_at: row.created_at,
          plaintext: TOMBSTONE_ENVELOPE_PLAINTEXT,
          deleted: true,
          pending: isOutgoing ? false : undefined,
          delivered: row.delivered,
          read: row.read,
          seenLocal,
        };
      }

      let plaintext = "";
      try {
        if (row.from_id === activeKeys.userId) {
          if (!row.sender_nonce || !row.sender_ciphertext) return null;
          plaintext = decryptDirectMessage(
            b64ToU8(row.sender_ciphertext),
            b64ToU8(row.sender_nonce),
            activeKeys.publicKeyX,
            activeKeys.secretKeyX,
          );
        } else {
          plaintext = decryptDirectMessage(
            b64ToU8(row.ciphertext),
            b64ToU8(row.nonce),
            b64ToU8(peer.public_key_x),
            activeKeys.secretKeyX,
          );
        }
      } catch {
        return null;
      }

      const focused = dmPeerIdFromConversation(selectedConversationRef.current) === peerId;
      const isOutgoing = row.from_id === activeKeys.userId;
      const seenLocal = isOutgoing ? focused : focused || row.read === true;

      return {
        id: row.id,
        peerId,
        from_id: row.from_id,
        to_id: row.to_id,
        created_at: row.created_at,
        plaintext,
        pending: isOutgoing ? false : undefined,
        delivered: row.delivered,
        read: row.read,
        seenLocal,
      };
    },
    [keysRef, selectedConversationRef],
  );
}
